import React, { useState, useRef, useEffect } from "react";
import {
  FaChevronDown,
  FaSortAmountDown,
  FaSortAlphaDown,
  FaArrowDown,
  FaArrowUp,
  FaTag,
} from "react-icons/fa";

export const SORT_OPTIONS = [
  {
    value: "default",
    label: "Default Sorting",
    sublabel: "Best match first",
    Icon: FaSortAmountDown,
    color: "#6366f1",
    bg: "#eef2ff",
  },
  {
    value: "name",
    label: "Name (A → Z)",
    sublabel: "Alphabetical order",
    Icon: FaSortAlphaDown,
    color: "#0ea5e9",
    bg: "#e0f2fe",
  },
  {
    value: "price-low",
    label: "Price: Low to High",
    sublabel: "Cheapest first",
    Icon: FaArrowUp,
    color: "#16a34a",
    bg: "#dcfce7",
  },
  {
    value: "price-high",
    label: "Price: High to Low",
    sublabel: "Most expensive first",
    Icon: FaArrowDown,
    color: "#dc2626",
    bg: "#fee2e2",
  },
  {
    value: "discount",
    label: "Best Discount",
    sublabel: "Biggest savings first",
    Icon: FaTag,
    color: "#f59e0b",
    bg: "#fef3c7",
  },
];

const SortDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selected = SORT_OPTIONS.find((o) => o.value === value) ?? SORT_OPTIONS[0];

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative w-full sm:w-auto" style={{ zIndex: 50 }}>

      {/* Trigger button — compact */}
      <button
        id="sort-dropdown-trigger"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="group flex w-full items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition-all duration-200 hover:border-primary-400 hover:shadow-md focus:outline-none sm:w-auto sm:min-w-[180px]"
        style={open ? { borderColor: selected.color, boxShadow: `0 0 0 2px ${selected.color}22` } : {}}
      >
        {/* Icon bubble */}
        <span
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-200"
          style={{ background: selected.bg, color: selected.color }}
        >
          <selected.Icon className="h-3 w-3" aria-hidden="true" />
        </span>

        {/* Label */}
        <span className="flex-1 text-left leading-tight">
          <span className="block text-[10px] font-medium text-gray-400">Sort by</span>
          <span className="block text-xs font-bold text-gray-800">{selected.label}</span>
        </span>

        {/* Chevron */}
        <FaChevronDown
          className="h-2.5 w-2.5 flex-shrink-0 text-gray-400 transition-transform duration-300"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown panel — compact */}
      {open && (
        <div
          role="listbox"
          aria-label="Sort options"
          className="absolute right-0 mt-1.5 w-52 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl"
          style={{ animation: "sortDropdownIn 0.16s ease", transformOrigin: "top right" }}
        >
          <div className="border-b border-gray-100 px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Sort Products</p>
          </div>
          <ul className="p-1.5">
            {SORT_OPTIONS.map((option) => {
              const isActive = option.value === value;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => handleSelect(option.value)}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition-all duration-150 hover:bg-gray-50 focus:outline-none"
                    style={isActive ? { background: option.bg } : {}}
                  >
                    {/* Icon */}
                    <span
                      className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-150"
                      style={{ background: isActive ? option.color : option.bg, color: isActive ? "#fff" : option.color }}
                    >
                      <option.Icon className="h-3 w-3" aria-hidden="true" />
                    </span>

                    {/* Text */}
                    <span className="flex-1">
                      <span className="block text-xs font-semibold" style={{ color: isActive ? option.color : "#1f2937" }}>
                        {option.label}
                      </span>
                      <span className="block text-[10px] text-gray-400">{option.sublabel}</span>
                    </span>

                    {/* Active tick */}
                    {isActive && (
                      <span
                        className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-white text-[9px] font-black"
                        style={{ background: option.color }}
                        aria-hidden="true"
                      >
                        ✓
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <style>{`
        @keyframes sortDropdownIn {
          from { opacity: 0; transform: scale(0.94) translateY(-5px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);    }
        }
      `}</style>
    </div>
  );
};

export default SortDropdown;
